/**
 * Which of the two families the lice pages display.
 *
 * The pages offer a toggle — "Raw data" against "Simulated manual count" — and it picks which
 * set of averages the charts read. On the wire the two are `*Avg` and `*LegacyAvg`, and the
 * naming is inverted relative to what they are: `*LegacyAvg` is the value calibrated to the
 * manual-count method, which is the one that gets reported. The backend has started calling
 * them `calibrated` and `raw`; this layer keeps the wire's names until that lands, so the
 * rename happens once rather than in two halves.
 */
export type LiceMeasurementSource = 'raw' | 'legacy';

/**
 * The six averages both the daily and the weekly payloads carry.
 *
 * Declared as its own shape so the selection below works on either, which is what four
 * containers were each re-declaring with a slightly different type parameter.
 */
export interface LiceMeasurementAverages {
    femaleAvg: number;
    movingStageAvg: number;
    caligusElongatusAvg?: number;
    femaleLegacyAvg: number;
    movingStageLegacyAvg: number;
    caligusElongatusLegacyAvg?: number;
}

/**
 * Copies the selected family onto the `*Avg` fields the charts read.
 *
 * Every other field is passed through, so a row keeps its day, its counts and anything a
 * caller hung on it. `'raw'` is a no-op and is written out anyway, because a selection that
 * only acts in one direction is the kind that later grows a bug.
 */
export function selectMeasurementSource<T extends LiceMeasurementAverages>(
    row: T,
    source: LiceMeasurementSource
): T {
    if (source !== 'legacy') {
        return row;
    }

    return {
        ...row,
        femaleAvg: row.femaleLegacyAvg,
        movingStageAvg: row.movingStageLegacyAvg,
        caligusElongatusAvg: row.caligusElongatusLegacyAvg,
    };
}

/**
 * The week-on-week changes the three-day snapshot carries, in both families.
 *
 * Required rather than optional, unlike the averages: the snapshot endpoint declares all six,
 * including the Caligus pair, even at sites that do not track it.
 */
export interface LiceMeasurementDiffs {
    femaleAvgDiff: number;
    movingStageAvgDiff: number;
    caligusElongatusAvgDiff: number;
    femaleLegacyAvgDiff: number;
    movingStageLegacyAvgDiff: number;
    caligusElongatusLegacyAvgDiff: number;
}

/**
 * The snapshot variant of {@link selectMeasurementSource}.
 *
 * The toggle has to reach the differences as well as the averages, or the arrow beside a count
 * disagrees with the number above it — it would keep pointing at last week's change in the
 * other family. Two containers each carried their own copy of this.
 */
export function selectSnapshotMeasurementSource<
    T extends LiceMeasurementAverages & LiceMeasurementDiffs,
>(row: T, source: LiceMeasurementSource): T {
    if (source !== 'legacy') {
        return row;
    }

    return {
        ...selectMeasurementSource(row, source),
        femaleAvgDiff: row.femaleLegacyAvgDiff,
        movingStageAvgDiff: row.movingStageLegacyAvgDiff,
        caligusElongatusAvgDiff: row.caligusElongatusLegacyAvgDiff,
    };
}
